/* eslint-disable react/no-danger */

import 'react-day-picker/style.css';

import React, { type ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import {
  type LayoutChangeEvent,
  Pressable,
  StyleSheet,
  View,
  type ViewInstance,
} from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme, TYPE_CONFIG } from '@garnish/constants';
import { useBrowserEventListener } from '@garnish/core';
import { usePressableState } from '@garnish/core';
import { useToggleState } from '@garnish/core';
import { useWebKeyListener } from '@garnish/core';
import { webOnlyStyles } from '@garnish/core';
import { Icon } from '@garnish/core';
import { HStack } from '@garnish/core';
import { BodyText } from '@garnish/core';
import { Select } from '@garnish/forms';
import { format } from 'date-fns/format';

import { type DateAndHourSelectorsProps } from './DateAndHourSelectors.types';

import type { ValueModel as SelectedValue } from '@garnish/forms/src/components/Select/Select.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `react-day-picker` based date picker component.
 *
 * NOTE: `DateAndHourSelectors` is web-only and is used on large breakpoints.
 *       `DateAndHourPickerGroup` is used in other scenarios.
 */
export const DateAndHourSelectors = (props: DateAndHourSelectorsProps) => {
  const {
    fromDate,
    toDate,
    selectedDate,
    selectedDateHourOptions,
    'aria-label': ariaLabel,
    labels,
    onChange,
  } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const datePickerContainerRef = useRef<ViewInstance>(null);
  const datePickerTriggerRef = useRef<ViewInstance>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isDatepickerShown,
    toggle: toggleIsDatepickerShown,
    toggleOff: hideDatepicker,
  } = useToggleState();
  const [offsetY, setOffsetY] = useState(theme.spacing['10']);

  // ─── Derived Data ────────────────────────────────────────────────────

  const hasSelectedDate = selectedDate !== undefined;
  const dateDynamicLabel = hasSelectedDate ? format(selectedDate, 'MM-dd-yyyy') : labels.day;
  const disabledDays = useMemo<DayPickerProps['disabled']>(
    () => (fromDate ? [{ before: fromDate }, { after: toDate }] : [{ after: toDate }]),
    [fromDate, toDate],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setDatePickerOffset = useCallback((event: LayoutChangeEvent) => {
    setOffsetY(event.nativeEvent.layout.height);
  }, []);

  const onDateChange = useCallback(
    (value: Date | undefined) => {
      onChange(value);
      hideDatepicker();
    },
    [onChange, hideDatepicker],
  );

  const onHourOptionChange = useCallback(
    (dateIsoString: SelectedValue) => {
      onChange(new Date(dateIsoString));
    },
    [onChange],
  );

  const dismissOnPressOutside = useCallback(
    (event: MouseEvent) => {
      const ref = datePickerContainerRef.current as unknown as HTMLDivElement;
      const targetNode = event.target as Node;
      const shouldCloseDatePicker = !ref?.contains(targetNode);

      if (!shouldCloseDatePicker) return;

      hideDatepicker();
    },
    [hideDatepicker],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  // @ts-expect-error TS(2345): Argument of type 'RefObject<View | null>' is not a... Remove this comment to see the full error message
  const datePickerTriggerState = usePressableState(datePickerTriggerRef);

  const datePickerContainerDynamicStyles = useStyle(
    () => ({ top: offsetY + theme.spacing['2'] }),
    [offsetY],
  );
  const datePickerContainerStyles = [styles.datePickerContainer, datePickerContainerDynamicStyles];

  const datePickerTriggerStyle = [
    styles.trigger,
    datePickerTriggerState.isHovered && styles.triggerHover,
    datePickerTriggerWebStyles,
  ];
  const labelStyles = hasSelectedDate ? styles.labelSelected : styles.label;

  // ─── Effects ─────────────────────────────────────────────────────────

  useBrowserEventListener(
    window.document,
    'click',
    dismissOnPressOutside,
    CLICK_EVENT_LISTENER_OPTIONS,
  );
  useWebKeyListener('Escape', hideDatepicker);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <View>
        <HStack itemsPerRow={2} gap={theme.spacing['6']}>
          <Pressable
            ref={datePickerTriggerRef}
            accessibilityRole="button"
            role="button"
            style={datePickerTriggerStyle}
            onPress={toggleIsDatepickerShown}
            aria-label={ariaLabel}
            onLayout={setDatePickerOffset}
          >
            <BodyText sizeMatch={['14']} style={labelStyles}>
              {dateDynamicLabel}
            </BodyText>

            <Icon name="IconCalendar" />
          </Pressable>

          <Select
            variation="box"
            value={selectedDate.toISOString()}
            options={selectedDateHourOptions}
            onSelect={onHourOptionChange}
            accessibilityLabel={labels.time}
          />
        </HStack>

        {isDatepickerShown ? (
          <View ref={datePickerContainerRef} style={datePickerContainerStyles}>
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={onDateChange}
              defaultMonth={selectedDate}
              startMonth={fromDate}
              endMonth={toDate}
              disabled={disabledDays}
              style={DAY_PICKER_STYLE_VARIABLES}
              formatters={DAY_PICKER_FORMATTERS}
              styles={DAY_PICKER_STYLES}
            />
          </View>
        ) : null}
      </View>

      {/* Because `react-day-picker` does not allow for full customization,
          we have included some inline CSS properties here. */}
      <style dangerouslySetInnerHTML={DAY_PICKER_INLINE_WEB_STYLES} />
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DAY_PICKER_FORMATTERS: DayPickerProps['formatters'] = {
  formatWeekdayName(date) {
    return format(date, 'E');
  },
};

const CLICK_EVENT_LISTENER_OPTIONS: AddEventListenerOptions = {
  capture: true, // Capture all `click` events, even those that should not be propagated.
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  datePickerContainer: {
    position: 'absolute',
    left: 0,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    paddingLeft: theme.spacing['2'],
    paddingRight: 6,
    borderRadius: theme.radius.small,
    minHeight: 32,
  },
  triggerHover: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  label: {
    color: theme.colors.CHARCOAL,
  },
  labelSelected: {
    color: theme.colors.BLACK,
  },
});

const datePickerTriggerWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms`,
});

const DAY_PICKER_BODY_TEXT = TYPE_CONFIG.BODY['16'];
const DAY_PICKER_NAV_BUTTON_STYLES = {
  width: 28,
  height: 28,
  backgroundColor: theme.colors.CREAM,
};

const DAY_PICKER_STYLES: DayPickerStyles = {
  root: {
    margin: 0,
    backgroundColor: theme.colors.WHITE,
    borderRadius: theme.radius.xlarge,
    padding: theme.spacing['4'],
    fontFamily: DAY_PICKER_BODY_TEXT.fontFamily,
    fontSize: DAY_PICKER_BODY_TEXT.fontSize,
    fontWeight: DAY_PICKER_BODY_TEXT.fontWeight,
    letterSpacing: DAY_PICKER_BODY_TEXT.letterSpacing,
    boxShadow: `0 8px 36px 0 ${theme.colors.OPACITY.DARK_KALE.LIGHTEST}`,
  },
  weekdays: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  caption_label: {
    color: theme.colors.SPINACH,
    fontFamily: DAY_PICKER_BODY_TEXT.fontFamily,
    fontWeight: DAY_PICKER_BODY_TEXT.fontWeight,
  },
  day: {
    color: theme.colors.TEXT_COLOR,
  },
  nav: {
    display: 'flex',
    gap: theme.spacing['2'],
  },
  button_next: DAY_PICKER_NAV_BUTTON_STYLES,
  button_previous: DAY_PICKER_NAV_BUTTON_STYLES,
  chevron: {
    fill: theme.colors.SPINACH,
  },
  month_caption: {
    marginBottom: theme.spacing['4'],
  },
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const DAY_PICKER_STYLE_VARIABLES = {
  '--rdp-selected-color': theme.colors.WHITE,
  '--rdp-background-color': theme.colors.CUCUMBER,
  '--rdp-accent-color': theme.colors.SPINACH,
  '--rdp-day-width': '36px',
  '--rdp-day-height': '36px',
  '--rdp-day_button-width': '36px',
  '--rdp-day_button-height': '36px',
  '--rdp-selected-border': '2px solid transparent',
  '--rdp-disabled-opacity': '0.25',
  '--rdp-nav_button-disabled-opacity': '0.25',
  '--rdp-nav-height': '28px',
} as DayPickerProps['style'];

// NOTE: Should be used in an inline `style` component as the
//       `dangerouslySetInnerHTML` property.
const DAY_PICKER_INLINE_WEB_STYLES: ComponentProps<'style'>['dangerouslySetInnerHTML'] = {
  __html: `
.rdp-root .rdp-month_caption {
  align-items: center;
}

.rdp-root .rdp-caption_label {
  margin: 0;
  padding: 0 0.25em;
  border: 2px solid transparent;
  font-size: 18px;
}

.rdp-root .rdp-button_previous,
.rdp-root .rdp-button_next {
  padding: 0.25em;
  border: 2px solid transparent;
  border-radius: 100%;
}

.rdp-root .rdp-chevron {
  width: 16px;
  height: 16px;
}

.rdp-root .rdp-weekday {
  height: var(--rdp-day-height);
  padding: 0;
  opacity: 1;
  font-size: 0.75em;
  font-weight: inherit;
  text-transform: uppercase;
  vertical-align: middle;
}

.rdp-root .rdp-day {
  padding: 0;
}

.rdp-root .rdp-today:not(.rdp-outside) > .rdp-day_button {
  font-weight: bold;
}

.rdp-root .rdp-selected {
  font-size: inherit;
  font-weight: inherit;
}

.rdp-root .rdp-selected > .rdp-day_button {
  font-weight: bold;
  color: var(--rdp-selected-color);
  background-color: var(--rdp-accent-color);
}

.rdp-root .rdp-day_button:focus-visible:not([disabled]),
.rdp-root .rdp-button_previous:focus-visible:not([aria-disabled='true']),
.rdp-root .rdp-button_next:focus-visible:not([aria-disabled='true']) {
  outline: none;
  color: inherit;
  background-color: var(--rdp-background-color);
  border: 2px solid var(--rdp-accent-color);
}

.rdp-root .rdp-selected > .rdp-day_button:focus-visible {
  outline: 2px solid var(--rdp-accent-color);
  outline-offset: 2px;
  z-index: 1;
}

.rdp-root .rdp-day:not(.rdp-selected) > .rdp-day_button:hover:not([disabled]),
.rdp-root .rdp-button_previous:hover:not([aria-disabled='true']),
.rdp-root .rdp-button_next:hover:not([aria-disabled='true']) {
  background-color: ${theme.colors.SPINACH} !important;
  color: ${theme.colors.WHITE} !important;
}

.rdp-root .rdp-button_previous:hover:not([aria-disabled='true']) > svg,
.rdp-root .rdp-button_next:hover:not([aria-disabled='true']) > svg {
  fill: ${theme.colors.WHITE} !important;
}`,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type DayPickerStyles = DayPickerProps['styles'];

type DayPickerProps = ComponentProps<typeof DayPicker>;
